import React, { useEffect, useState, useMemo } from "react"
import api from "../services/api"
import { Helmet } from "react-helmet"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { UploadCloud, EditIcon } from "lucide-react"
import Skeleton from "./skeleton/Skeleton"
import Swal from "sweetalert2"

export default function Billings() {
  const [errors, setErrors] = useState({})
  const [tenants, setTenants] = useState([])
  const [units, setUnits] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [rooms, setRooms] = useState([])
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [tenantToDelete, setTenantToDelete] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState(null)
  const [isViewReceiptOpen, setIsViewReceiptOpen] = useState(false)
  const [viewReceiptUrl, setViewReceiptUrl] = useState("")
  const [filterStatus, setFilterStatus] = useState("All")
  const [filterMode, setFilterMode] = useState("All")
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [processOfPayment, setProcessOfPayment] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState("")
  const [paymentError, setPaymentError] = useState("")
  const [loading, setLoading] = useState(true)

  const PAGE_SIZE = 5

  const openViewReceipt = (url) => {
    if (!url) return
    setViewReceiptUrl(url)
    setIsViewReceiptOpen(true)
  }

  const closeViewReceipt = () => {
    setIsViewReceiptOpen(false)
    setViewReceiptUrl("")
  }

  // Receipt edit modal
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false)
  const [receiptTenantId, setReceiptTenantId] = useState(null)
  const [receiptFile, setReceiptFile] = useState(null)
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState("")

  // FLOW B additions
  const getCurrentPeriod = () => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    return `${y}-${m}` // YYYY-MM
  }

  const [period, setPeriod] = useState(getCurrentPeriod())
  const [paymentsByTenant, setPaymentsByTenant] = useState({}) // { [tenantId]: payment }

  const phoneRegex = /^09\d{9}$/

  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    email: "",
    unit: "",
    moveInDate: "",
    contractEnd: "",
    emergencyContact: "",
  })

  const emptyForm = {
    name: "",
    contact: "",
    email: "",
    unit: "",
    moveInDate: "",
    contractEnd: "",
    emergencyContact: "",
  }

  const isPaymentFormValid = useMemo(() => {
    if (!selectedPayment) return false

    const amount = Number(paymentAmount)
    const amort = Number(selectedPayment.monthlyAmortization)

    // mode required
    if (
      !selectedPayment.modeOfPayment ||
      selectedPayment.modeOfPayment === "N/A"
    ) {
      return false
    }

    // amount required
    if (!amount || amount <= 0) return false

    // receipt required for e-wallet
    if (
      selectedPayment.modeOfPayment === "E-Wallet" &&
      !selectedPayment.receiptFile
    ) {
      return false
    }
    if (
      selectedPayment.modeOfPayment === "E-Wallet" &&
      !selectedPayment.receiptFile
    ) {
      return false
    }

    return true
  }, [selectedPayment, paymentAmount])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = "Tenant name is required"

    if (!formData.contact.trim()) {
      newErrors.contact = "Contact number is required"
    } else if (!phoneRegex.test(formData.contact)) {
      newErrors.contact = "Contact number must be 11 digits (09xxxxxxxxx)"
    }

    if (!formData.moveInDate) newErrors.moveInDate = "Move-in date is required"

    if (!formData.contractEnd) {
      newErrors.contractEnd = "Contract end date is required"
    } else if (
      formData.moveInDate &&
      new Date(formData.contractEnd) <= new Date(formData.moveInDate)
    ) {
      newErrors.contractEnd = "Contract end date must be after move-in date"
    }

    if (!formData.emergencyContact.trim()) {
      newErrors.emergencyContact = "Emergency contact is required"
    } else if (!phoneRegex.test(formData.emergencyContact)) {
      newErrors.emergencyContact =
        "Contact number must be 11 digits (09xxxxxxxxx)"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // ---------------------------
  // FETCH TENANTS
  // ---------------------------
  const fetchTenants = async () => {
    try {
      const res = await api.get(`/api/tenants`)

      setTenants(res.data) // already parsed
    } catch (err) {
      console.error("Failed to fetch tenants", err.response || err)
    }
  }

  // ---------------------------
  // FETCH UNITS
  // ---------------------------

  const fetchUnits = async () => {
    try {
      const res = await api.get(`/api/units`)
      setUnits(res.data)
      console.log(res.data)
    } catch (err) {
      console.error("Failed to fetch units", err)
    }
  }

  // ---------------------------
  // FLOW B: FETCH PAYMENTS for period
  // Expected backend: GET /api/payments?period=YYYY-MM
  // Returns array: [{ tenant, unit, period, status, modeOfPayment, receipt, ...}]
  // ---------------------------
  const fetchPayments = async (periodValue) => {
    try {
      const res = await api.get(`/api/billings?period=${periodValue}`)
      const map = {}
      for (const p of res.data) {
        const tenantId = typeof p.tenant === "string" ? p.tenant : p.tenant?._id
        if (tenantId) map[tenantId] = p
      }
      setPaymentsByTenant(map)
    } catch (err) {
      console.error("Failed to fetch payments", err)
    }
  }

  // Derive payment display object for a tenant
  const getTenantPayment = (tenant) => {
    const p = paymentsByTenant[tenant._id]
    return (
      p || {
        _id: null,
        tenant: tenant._id,
        unit: tenant.unit,
        period,
        status: "Unpaid",
        modeOfPayment: "N/A",
        receipt: null,
        name: tenant.name,
        monthlyAmortization: tenant.monthlyAmortization,
      }
    )
  }

  // Build list of tenants that are assigned to a unit
  const assignedTenants = useMemo(() => {
    const tenantIdToUnit = new Map()

    for (const u of units) {
      const tenantId = typeof u.tenant === "object" ? u.tenant?._id : u.tenant

      if (tenantId) {
        tenantIdToUnit.set(tenantId, u)
      }
    }

    return tenants
      .filter((t) => tenantIdToUnit.has(t._id))
      .map((t) => {
        const assignedUnit = tenantIdToUnit.get(t._id)

        return {
          ...t,
          assignedUnit,
          monthlyAmortization:
            assignedUnit?.monthlyAmortization ?? "Not Available",
        }
      })
  }, [tenants, units])

  const filteredTenants = assignedTenants.filter((tenant) => {
    const payment = getTenantPayment(tenant)

    const matchesStatus =
      filterStatus === "All" ? true : payment.status === filterStatus

    const mode = payment.modeOfPayment || "N/A"
    const matchesMode = filterMode === "All" ? true : mode === filterMode

    return matchesStatus && matchesMode
  })

  const totalPages = Math.max(1, Math.ceil(filteredTenants.length / PAGE_SIZE))
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const paginatedTenants = filteredTenants.slice(
    startIndex,
    startIndex + PAGE_SIZE
  )

  // FLOW B: UPSERT Payment
  // Expected backend: PUT /api/billing
  // body: { tenantId, unitId, period, status?, modeOfPayment? }
  const upsertPayment = async ({ tenantId, unitId, updates }) => {
    try {
      const tenant = tenants.find((t) => t._id === tenantId)
      const monthlyAmortization = tenant ? tenant.monthlyAmortization : null

      const res = await api.put(`/api/billings`, {
        tenantId,
        unitId,
        period,
        name: tenant?.name,
        monthlyAmortization,
        ...updates,
      })
      const saved = res.data
      setPaymentsByTenant((prev) => ({ ...prev, [tenantId]: saved }))
    } catch (err) {
      console.error("Failed to update payment", err)
      Swal.fire({
        icon: "error",
        title: "Payment update failed",
        text: "Could not update payment info.",
      })
      throw err
    }
  }

  // FLOW B: Upload Receipt (multipart)
  // Expected backend: PUT /api/billing/receipt
  // formData: tenantId, unitId, period, receipt(file)
  const uploadReceipt = async ({ tenantId, file }) => {
    const fd = new FormData()
    fd.append("tenantId", tenantId)
    fd.append("period", period)
    fd.append("receipt", file)

    const res = await api.put(`/api/billings/receipt`, fd)

    const saved = res.data
    setPaymentsByTenant((prev) => ({ ...prev, [tenantId]: saved }))
  }

  // ---------------------------
  // FETCH ROOMS (unchanged)
  // ---------------------------
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get(`/api/rooms`)

        const filteredRooms = res.data.filter((room) =>
          room.units.every((unit) => unit.status !== "Occupied")
        )
        setRooms(filteredRooms)
      } catch (err) {
        console.error("Failed to load rooms", err)
      }
    }

    fetchRooms()
  }, [units])

  // Fetch tenants once
  useEffect(() => {
    fetchTenants()
    fetchUnits()
  }, [])

  // Fetch payments when period changes
  useEffect(() => {
    fetchPayments(period)
  }, [period])

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    setErrors((prev) => {
      if (!prev[name]) return prev
      const { [name]: _, ...rest } = prev
      return rest
    })
  }

  // Add tenant (unchanged)
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      await api.post(`/api/tenants`, formData)

      setIsAddModalOpen(false)
      setFormData(emptyForm)
      setErrors({})
      fetchTenants()
    } catch (err) {
      console.error("Failed to add tenant", err.response || err)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    if (!selectedTenantId) return

    try {
      const res = await api.put(`/api/tenants/${selectedTenantId}`, formData)

      const updated = res.data

      // update table immediately
      setTenants((prev) =>
        prev.map((t) => (t._id === updated._id ? updated : t))
      )

      setIsModalOpen(false)
      setSelectedTenantId(null)
      setFormData(emptyForm)
      setErrors({})

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Tenant details have been saved.",
        confirmButtonColor: "#f97316",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: "Something went wrong while saving changes.",
      })
    }
  }

  const confirmDelete = async () => {
    if (!tenantToDelete) return

    try {
      await api.delete(`/api/tenants/${tenantToDelete._id}`)

      setTenants((prev) => prev.filter((t) => t._id !== tenantToDelete._id))

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        confirmButtonColor: "#d1001f",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error(err)
      alert("Failed to delete tenant")
    } finally {
      setIsDeleteModalOpen(false)
      setTenantToDelete(null)
    }
  }

  const openReceiptModal = (tenant) => {
    const payment = getTenantPayment(tenant)

    setReceiptTenantId(tenant._id)
    setReceiptFile(null)
    setReceiptPreviewUrl(payment?.receipt?.url || "")
    setIsReceiptModalOpen(true)
  }

  const closeReceiptModal = () => {
    setIsReceiptModalOpen(false)
    setReceiptTenantId(null)
    setReceiptFile(null)
    setReceiptPreviewUrl("")
  }

  // OPTIONAL: if your backend supports removing receipt
  // Expected backend example: DELETE /api/payments/receipt?tenantId=...&period=...
  const removeReceipt = async (tenantId) => {
    Swal.fire({
      title: "Deleting receipt...",
      text: "Please wait",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    try {
      await api.delete(
        `/api/billings/receipt?tenantId=${tenantId}&period=${period}`
      )
      setPaymentsByTenant((prev) => ({
        ...prev,
        [tenantId]: { ...prev[tenantId], receipt: null },
      }))
      Swal.close()
      Swal.fire({
        icon: "success",
        title: "Receipt removed",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error("Failed to remove receipt", err)
      Swal.close()
      Swal.fire({
        icon: "error",
        title: "Remove failed",
        text: "Could not remove receipt.",
      })
    }
  }

  const handleExportExcel = () => {
    const rows = filteredTenants.map((tenant) => {
      const payment = getTenantPayment(tenant)

      return {
        Period: period,
        Tenant: tenant.name ?? "",
        Email: tenant.email ?? "",
        Unit:
          tenant.assignedUnit?.unitNumber ??
          tenant.assignedUnit?.roomNumber ??
          tenant.unit ??
          "",
        Status: payment.status ?? "Unpaid",
        "Mode of Payment": payment.modeOfPayment ?? "N/A",
        "Receipt URL": payment.receipt?.url ?? "",
        "Move In Date": tenant.moveInDate
          ? new Date(tenant.moveInDate).toLocaleDateString()
          : "",
        "Contract End": tenant.contractEnd
          ? new Date(tenant.contractEnd).toLocaleDateString()
          : "",
        Contact: tenant.contact ?? "",
        "Emergency Contact": tenant.emergencyContact ?? "",
      }
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, `Payments-${period}`)

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    saveAs(blob, `payments-${period}.xlsx`)
  }

  useEffect(() => {
    if (Object.keys(errors).length === 0) return
    const timer = setTimeout(() => setErrors({}), 5000)
    return () => clearTimeout(timer)
  }, [errors])

  useEffect(() => {
    setCurrentPage(1)
  }, [period, filterStatus, filterMode, tenants, units, paymentsByTenant])

  const assignedRoomNumbers = useMemo(() => {
    return new Set(units.map((u) => u.unitNumber).filter(Boolean))
  }, [units])

  const availableRooms = useMemo(() => {
    return rooms.filter((room) => !assignedRoomNumbers.has(room.roomNumber))
  }, [rooms, assignedRoomNumbers])

  const availableRoomsForEdit = useMemo(() => {
    if (!selectedUnit) return availableRooms
    return rooms.filter(
      (room) =>
        room.roomNumber === selectedUnit.unitNumber ||
        !assignedRoomNumbers.has(room.roomNumber)
    )
  }, [rooms, availableRooms, assignedRoomNumbers, selectedUnit])

  const formatDate = (dateString) => {
    if (!dateString) return "—"

    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      try {
        await Promise.all([fetchTenants(), fetchUnits()])

        await fetchPayments(period)
      } catch (err) {
        console.error("Initial load failed", err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  return (
    <>
      <Helmet>
        <title>Billings - Stay Suite | Apartment Management System</title>
      </Helmet>
      <div className="w-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {loading ? (
          <Skeleton />
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-2">Billings</h1>
            <p className="pb-5 text-gray-600 italic text-sm">
              Provide the required details below and attach the payment receipt
              for verification.
            </p>

            {/* Period selector (Flow B) */}
            <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Billing Period:</label>
                <input
                  type="month"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="bg-white h-10 rounded-lg border border-gray-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-500"
                />
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <label className="text-sm text-gray-600 ml-2">Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-white h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="All">All</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Paid">Paid</option>
                  <option value="Partial">Partial</option>
                </select>

                <label className="text-sm text-gray-600 ml-2">Mode:</label>
                <select
                  value={filterMode}
                  onChange={(e) => setFilterMode(e.target.value)}
                  className="bg-white h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="All">All</option>
                  <option value="N/A">N/A</option>
                  <option value="Cash">Cash</option>
                  <option value="E-Wallet">E-Wallet</option>
                </select>

                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="h-10 px-3 rounded-lg bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700 w-full sm:w-auto"
                >
                  Export Excel
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setFilterStatus("All")
                    setFilterMode("All")
                  }}
                  className="h-10 px-3 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white mt-6">
              <div className="w-full overflow-x-auto rounded-lg  bg-white">
                <table className="min-w-160 w-full border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Payment Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Tenant
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Payment Status
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Monthly Ammortation
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Mode of Payment
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Payment
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap"></th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* ✅ NO DATA FOUND HERE */}
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {filteredTenants.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-10 text-center text-gray-500 italic"
                        >
                          No data found
                        </td>
                      </tr>
                    ) : (
                      paginatedTenants.map((tenant, index) => {
                        const actualIndex = startIndex + index
                        const payment = getTenantPayment(tenant)

                        return (
                          <tr
                            key={tenant._id}
                            className={
                              actualIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-4 py-3">
                              {formatDate(payment.createdAt)}
                            </td>
                            <td className="px-4 py-3">{tenant.name}</td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <span
                                className={`${
                                  payment.status === "Paid"
                                    ? "bg-green-100 text-green-700 ring-1 ring-green-200 rounded-2xl px-3 py-1 text-xs"
                                    : payment.status === "Unpaid"
                                      ? "bg-red-100 text-red-500 ring-1 ring-red-200 rounded-2xl px-3 py-1 text-xs"
                                      : "bg-yellow-100 text-yellow-700 ring-1 ring-yellow-200 rounded-2xl px-3 py-1 text-xs"
                                }`}
                              >
                                {payment.status}
                              </span>
                            </td>

                            <td className=" px-4 py-3">
                              ₱
                              {Number(
                                tenant.monthlyAmortization || 0
                              ).toLocaleString()}
                            </td>

                            <td className=" px-4 py-3">
                              {payment.modeOfPayment || "Not Available"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium">
                                ₱
                                {Number(
                                  payment.amountPaid || 0
                                ).toLocaleString()}{" "}
                                / ₱
                                {Number(
                                  tenant.monthlyAmortization || 0
                                ).toLocaleString()}
                              </div>

                              {Number(payment.amountPaid || 0) <
                                Number(tenant.monthlyAmortization || 0) && (
                                <div className="text-xs text-red-500">
                                  Balance: ₱
                                  {(
                                    Number(tenant.monthlyAmortization || 0) -
                                    Number(payment.amountPaid || 0)
                                  ).toLocaleString()}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3">
                              {payment.status !== "Paid" && (
                                <button
                                  disabled={payment.status === "Paid"}
                                  onClick={() => {
                                    const payment = getTenantPayment(tenant)

                                    setSelectedPayment({
                                      tenantId: tenant._id,
                                      unitId: tenant.assignedUnit?._id,
                                      modeOfPayment:
                                        payment.modeOfPayment || "N/A",
                                      receipt: payment.receipt || null,
                                      monthlyAmortization:
                                        Number(tenant.monthlyAmortization) || 0,
                                    })

                                    setProcessOfPayment(true)
                                  }}
                                  className={`h-10 w-full sm:w-auto rounded-lg px-5 text-sm font-semibold shadow-sm
    ${
      payment.status === "Paid"
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-orange-500 text-white hover:bg-orange-600"
    }
  `}
                                >
                                  {payment.status === "Paid"
                                    ? "Paid"
                                    : "Payment"}
                                </button>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 ">
                              {payment.modeOfPayment === "E-Wallet" &&
                              payment.receipt ? (
                                <button
                                  onClick={() => openReceiptModal(tenant)}
                                  className="p-2 rounded-lg text-orange-600 transition mr-2"
                                >
                                  <EditIcon size={20} />
                                </button>
                              ) : (
                                <button
                                  className="p-2 rounded-lg text-gray-400 transition mr-2"
                                  disabled
                                >
                                  <EditIcon size={20} />
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {filteredTenants.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t bg-white border-t-amber-400">
                  <p className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-medium">{startIndex + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(startIndex + PAGE_SIZE, filteredTenants.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">
                      {filteredTenants.length}
                    </span>
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Prev
                    </button>

                    <span className="text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span> of{" "}
                      <span className="font-medium">{totalPages}</span>
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {processOfPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Process of Payment
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Fill the details below to proceed payment
            </p>

            <form className="space-y-4" autoComplete="off">
              <div className="space-y-1">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Mode of Payment
                </label>
                <select
                  value={selectedPayment?.modeOfPayment || "N/A"}
                  onChange={(e) =>
                    setSelectedPayment((prev) => ({
                      ...prev,
                      modeOfPayment: e.target.value,
                    }))
                  }
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                >
                  <option value="N/A">N/A</option>
                  <option value="Cash">Cash</option>
                  <option value="E-Wallet">E-Wallet</option>
                </select>

                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>
              {selectedPayment?.modeOfPayment === "E-Wallet" && (
                <div className="space-y-1">
                  <label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Upload Receipt
                  </label>
                  <label
                    className="flex flex-col items-center justify-center w-full
    h-28 2xl:h-32
    border-2 border-dashed border-gray-300 rounded-xl
    cursor-pointer bg-gray-50
    hover:bg-gray-100 hover:border-blue-400
    transition text-gray-500"
                  >
                    <UploadCloud className="w-12 h-12 pb-2" />
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold text-blue-600">
                        Click to upload{" "}
                      </span>
                      or drag & drop
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      PNG, JPG - Max 1MB
                    </p>

                    <input
                      type="file"
                      className="hidden"
                      accept=".png,.jpg,.jpeg"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return

                        setSelectedPayment((prev) => ({
                          ...prev,
                          receiptFile: file,
                        }))
                      }}
                    />
                  </label>

                  {selectedPayment?.receiptFile && (
                    <p className="text-xs text-gray-500">
                      Selected: {selectedPayment.receiptFile.name}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <label
                  htmlFor="amount"
                  className="text-sm font-medium text-gray-700"
                >
                  Amount
                </label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                  placeholder="Please input amount"
                  max={50000}
                />

                {paymentError && (
                  <p className="text-xs text-red-500 italic">{paymentError}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setProcessOfPayment(false)
                  }}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-300
                        text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={!isPaymentFormValid}
                  onClick={async () => {
                    if (!selectedPayment) return

                    const payment = paymentsByTenant[selectedPayment.tenantId]

                    const prevPaid = Number(payment?.amountPaid || 0)
                    const inputAmount = Number(paymentAmount)

                    if (inputAmount <= 0) {
                      setPaymentError("Invalid payment amount")
                      return
                    }

                    const totalPaid = prevPaid + inputAmount
                    const monthly = Number(selectedPayment.monthlyAmortization)

                    let status = "Unpaid"

                    if (totalPaid >= monthly) {
                      status = "Paid"
                    } else if (totalPaid > 0) {
                      status = "Partial"
                    }

                    const result = await Swal.fire({
                      title: "Confirm payment?",
                      html: `
      <div style="text-align:center">
        <div><b>Previous Paid:</b> ₱${prevPaid.toLocaleString()}</div>
        <div><b>Paying Now:</b> ₱${inputAmount.toLocaleString()}</div>
        <hr />
        <div><b>Total Paid:</b> ₱${totalPaid.toLocaleString()}</div>
        <div><b>Status:</b> ${status}</div>
      </div>
    `,
                      showCancelButton: true,
                      confirmButtonText: "Confirm",
                      cancelButtonText: "Cancel",
                      confirmButtonColor: "#f97316",
                      reverseButtons: true,
                    })

                    if (!result.isConfirmed) return

                    Swal.fire({
                      title: "Processing payment...",
                      allowOutsideClick: false,
                      allowEscapeKey: false,
                      didOpen: () => Swal.showLoading(),
                    })

                    try {
                      await upsertPayment({
                        tenantId: selectedPayment.tenantId,
                        unitId: selectedPayment.unitId,
                        updates: {
                          modeOfPayment: selectedPayment.modeOfPayment,
                          amountPaid: totalPaid,
                          status,
                        },
                      })

                      if (selectedPayment.receiptFile) {
                        await uploadReceipt({
                          tenantId: selectedPayment.tenantId,
                          file: selectedPayment.receiptFile,
                        })
                      }

                      Swal.close()

                      await Swal.fire({
                        icon: "success",
                        title: "Payment recorded",
                        timer: 1300,
                        showConfirmButton: false,
                        confirmButtonColor: "#f97316",
                      })

                      setProcessOfPayment(false)
                      setSelectedPayment(null)
                      setPaymentAmount("")
                      setPaymentError("")
                    } catch (err) {
                      Swal.close()
                      Swal.fire({
                        icon: "error",
                        title: "Payment failed",
                        text: "Something went wrong.",
                        confirmButtonColor: "#f97316",
                      })
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition
    ${
      !isPaymentFormValid
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-orange-600 text-white hover:bg-orange-700"
    }
  `}
                >
                  Confirm Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ADD MODAL */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Assign Tenant
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Fill in tenant details below
            </p>

            <form
              className="space-y-4"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div className="space-y-1">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Tenant Name
                </label>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition"
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Contact Number
                </label>
                <input
                  type="tel"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="09xxxxxxxxx"
                  maxLength={11}
                  pattern="[0-9]{11}"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition"
                />
                {errors.contact && (
                  <p className="text-xs text-red-500">{errors.contact}</p>
                )}
              </div>

              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Emergency Contact
                </label>
                <input
                  type="tel"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  maxLength={11}
                  pattern="[0-9]{11}"
                  placeholder="Emergency contact number"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition"
                />
                {errors.emergencyContact && (
                  <p className="text-xs text-red-500">
                    {errors.emergencyContact}
                  </p>
                )}
              </div>

              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition"
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Move In Date
                </label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition"
                />
                {errors.moveInDate && (
                  <p className="text-xs text-red-500">{errors.moveInDate}</p>
                )}
              </div>

              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Contract End Date
                </label>
                <input
                  type="date"
                  name="contractEnd"
                  value={formData.contractEnd}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition"
                />
                {errors.contractEnd && (
                  <p className="text-xs text-red-500">{errors.contractEnd}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false)
                    setFormData(emptyForm)
                    setErrors({})
                  }}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-300
                        text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold
                        bg-orange-600 text-white hover:bg-orange-700
                        focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
                        transition"
                >
                  Add Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Delete Tenant
            </h2>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete tenant{" "}
              <span className="font-semibold">{tenantToDelete?.name}</span>?
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setTenantToDelete(null)
                }}
                className="px-4 py-2 rounded-lg border text-sm
                      text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-sm font-semibold
                      bg-red-600 text-white hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RECEIPT EDIT MODAL */}
      {isReceiptModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Edit Receipt
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Update receipt for this billing period ({period})
            </p>

            <div className="space-y-4">
              {/* Current Receipt */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Current Receipt
                </label>

                {receiptPreviewUrl ? (
                  <div className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-gray-50">
                    <img
                      src={receiptPreviewUrl}
                      alt="Receipt preview"
                      className="mt-3 max-h-56 w-full object-contain rounded cursor-zoom-in"
                      onClick={() => openViewReceipt(receiptPreviewUrl)}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />

                    <div className="text-center py-2">
                      <button
                        type="button"
                        onClick={() => openViewReceipt(receiptPreviewUrl)}
                        className="text-blue-600 hover:underline"
                      >
                        View current receipt
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-500 italic bg-gray-50">
                    No receipt uploaded.
                  </div>
                )}
              </div>

              {/* Replace Receipt */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Replace Receipt
                </label>

                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    setReceiptFile(f)
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                transition"
                />

                {receiptFile && (
                  <p className="text-xs text-gray-500">
                    Selected:{" "}
                    <span className="font-medium">{receiptFile.name}</span>
                  </p>
                )}
              </div>

              {/* Optional: Remove Receipt (kept but styled subtle) */}
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => {
                    if (!receiptTenantId) return
                    removeReceipt(receiptTenantId)
                    closeReceiptModal()
                  }}
                  className="text-sm font-medium text-red-600 hover:text-red-700 transition"
                >
                  Remove receipt
                </button>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeReceiptModal}
                    className="px-4 py-2 rounded-lg text-sm border border-gray-300
                  text-gray-700 hover:bg-gray-100 transition"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      if (!receiptTenantId) return

                      if (!receiptFile) {
                        Swal.fire({
                          icon: "info",
                          title: "No file selected",
                          text: "Choose a file first to replace the receipt.",
                          confirmButtonColor: "#f97316",
                        })
                        return
                      }

                      await uploadReceipt({
                        tenantId: receiptTenantId,
                        file: receiptFile,
                      })
                      closeReceiptModal()
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold
                  bg-orange-600 text-white hover:bg-orange-700
                  focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
                  transition"
                  >
                    Save Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW RECEIPT MODAL */}
      {isViewReceiptOpen && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={closeViewReceipt}
        >
          <div
            className="relative bg-white w-[85vw] max-w-3xl rounded-xl shadow-2xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeViewReceipt}
              className="absolute top-2 right-2 px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>

            <div className="mt-8">
              <img
                src={viewReceiptUrl}
                alt="Receipt"
                className="max-h-[75vh] w-full object-contain rounded"
              />
            </div>

            <div className="mt-3 flex justify-end gap-2">
              <a
                href={viewReceiptUrl}
                download
                className="px-4 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200"
              >
                Download
              </a>
              <button
                type="button"
                onClick={closeViewReceipt}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Edit Tenant
            </h2>
            <p className="text-sm text-gray-500 mb-5">Update tenant details</p>

            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:ring-2 focus:ring-orange-500 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Contact
                </label>
                <input
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  maxLength={11}
                  pattern="[0-9]{11}"
                  className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:ring-2 focus:ring-orange-500 ${
                  errors.contact ? "border-red-500" : "border-gray-300"
                }`}
                />
                {errors.contact && (
                  <p className="text-xs text-red-500">{errors.contact}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Emergency Contact
                </label>
                <input
                  type="tel"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  maxLength={11}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:ring-2 focus:ring-orange-500 ${
                  errors.emergencyContact ? "border-red-500" : "border-gray-300"
                }`}
                />
                {errors.emergencyContact && (
                  <p className="text-xs text-red-500">
                    {errors.emergencyContact}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:ring-2 focus:ring-orange-500 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Move In Date
                </label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:ring-2 focus:ring-orange-500 ${
                  errors.moveInDate ? "border-red-500" : "border-gray-300"
                }`}
                />
                {errors.moveInDate && (
                  <p className="text-xs text-red-500">{errors.moveInDate}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Contract End
                </label>
                <input
                  type="date"
                  name="contractEnd"
                  value={formData.contractEnd}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:ring-2 focus:ring-orange-500 ${
                  errors.contractEnd ? "border-red-500" : "border-gray-300"
                }`}
                />
                {errors.contractEnd && (
                  <p className="text-xs text-red-500">{errors.contractEnd}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false)
                    setFormData(emptyForm)
                    setErrors({})
                  }}
                  className="px-4 py-2 rounded-lg border text-sm
                        text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold
                        bg-orange-600 text-white hover:bg-orange-700 transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
